
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CreditCard, Wallet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface PaymentOptionsProps {
  credits: number;
  onUseCredit: () => void;
  onDirectPayment: () => void;
  onPurchaseCredits: () => void;
  isProcessing?: boolean;
}

const PaymentOptions = ({ 
  credits, 
  onUseCredit, 
  onDirectPayment, 
  onPurchaseCredits,
  isProcessing = false 
}: PaymentOptionsProps) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const { toast } = useToast();

  const handleDirectPayment = () => {
    setIsProcessingPayment(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessingPayment(false);
      onDirectPayment();
      toast({
        title: "Payment Successful",
        description: "Your resume is being downloaded!",
      });
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <h3 className="font-medium mb-2">Choose a download option:</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {credits > 0 && (
            <Button 
              variant="outline" 
              className="border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700" 
              onClick={onUseCredit}
              disabled={isProcessing}
            >
              <Wallet className="mr-2 h-4 w-4" />
              {isProcessing ? "Processing..." : "Use 1 Credit"}
              <div className="ml-2 bg-blue-100 px-2 py-0.5 rounded-full">
                <span className="text-lg font-bold text-blue-700">{credits}</span>
                <span className="text-xs text-blue-600 ml-1">available</span>
              </div>
            </Button>
          )}
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={isProcessing}>
                <CreditCard className="mr-2 h-4 w-4" />
                Pay $1.00 to Download
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Direct Payment</AlertDialogTitle>
                <AlertDialogDescription>
                  You'll be charged $1.00 to download this resume. Would you like to proceed with the payment?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDirectPayment}
                  disabled={isProcessingPayment}
                >
                  {isProcessingPayment ? "Processing..." : "Pay $1.00"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        
        {credits === 0 && (
          <div className="mt-4 text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
            <p>You have 0 credits. <button onClick={onPurchaseCredits} className="text-blue-600 underline">Buy credits in bulk</button> to save on fees!</p>
          </div>
        )}
      </div>
      
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <h4 className="font-medium mb-2">Why buy credits?</h4>
        <ul className="text-sm text-gray-600 space-y-2">
          <li>• Save on payment processing fees</li>
          <li>• Get bonus credits with bulk purchases</li>
          <li>• Streamline your resume downloads</li>
        </ul>
      </div>
    </div>
  );
};

export default PaymentOptions;
